import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import TemplateBuilder from '../components/TemplateBuilder';
import { Paintbrush, Upload, Save, Lock, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function WhiteLabelPage() {
  const { user, api } = useAuth();
  const [settings, setSettings] = useState({
    company_name: '',
    logo_url: '',
    primary_color: '#6366f1',
    secondary_color: '#ec4899',
    custom_domain: '',
    favicon_url: '',
    support_email: '',
    footer_text: ''
  });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);

  const isEnterprisePlus = user?.tier === 'enterprise';

  const handleSave = async () => {
    setLoading(true);
    // In production, save to backend
    setTimeout(() => {
      toast.success('White-label settings saved!');
      setLoading(false);
    }, 1000);
  };

  if (!isEnterprisePlus) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="whitelabel-locked">
          <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mb-6">
            <Lock className="w-10 h-10 text-zinc-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">White-Label Branding</h1>
          <p className="text-zinc-400 mb-6 max-w-md">
            Rebrand Insite360 as your own platform with custom logo, colors, domain, and branding.
          </p>
          <p className="text-amber-400 text-sm mb-6">
            This feature is available exclusively for Enterprise Plus subscribers.
          </p>
          <Link to="/billing">
            <Button className="btn-primary">Upgrade to Enterprise Plus</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="whitelabel-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">White-Label Branding</h1>
            <p className="text-zinc-400">Customize the platform with your own branding</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setPreview(!preview)}
            className="btn-secondary"
          >
            <Eye className="w-4 h-4 mr-2" />
            {preview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </div>

        {/* Preview */}
        {preview && (
          <div className="glass-card rounded-xl p-6 border-2 border-dashed border-indigo-500/30">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-4">Preview</p>
            <div className="bg-zinc-950 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Logo" className="h-8 w-auto" />
                ) : (
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: settings.primary_color }}
                  >
                    {settings.company_name?.charAt(0) || 'I'}
                  </div>
                )}
                <span className="font-bold text-white">{settings.company_name || 'Your Company'}</span>
              </div>
              <div className="flex gap-2">
                <button 
                  className="px-4 py-2 rounded-lg text-white text-sm"
                  style={{ backgroundColor: settings.primary_color }}
                >
                  Primary Button
                </button>
                <button 
                  className="px-4 py-2 rounded-lg text-white text-sm"
                  style={{ backgroundColor: settings.secondary_color }}
                >
                  Secondary Button
                </button>
              </div>
              {settings.footer_text && (
                <p className="text-xs text-zinc-500 mt-4">{settings.footer_text}</p>
              )}
            </div>
          </div>
        )}

        {/* Settings Form */}
        <div className="glass-card rounded-xl p-6 space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Paintbrush className="w-5 h-5 text-indigo-400" />
            <h3 className="text-lg font-semibold text-white">Branding Settings</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-zinc-300">Company Name</Label>
              <Input
                placeholder="Your Company Name"
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                className="input-dark mt-1"
                data-testid="whitelabel-company-input"
              />
            </div>

            <div>
              <Label className="text-zinc-300">Custom Domain</Label>
              <Input
                placeholder="compliance.yourcompany.com"
                value={settings.custom_domain}
                onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value })}
                className="input-dark mt-1"
                data-testid="whitelabel-domain-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Contact support to configure DNS</p>
            </div>

            <div>
              <Label className="text-zinc-300">Logo URL</Label>
              <Input
                placeholder="https://yourcompany.com/logo.png"
                value={settings.logo_url}
                onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                className="input-dark mt-1"
              />
            </div>

            <div>
              <Label className="text-zinc-300">Favicon URL</Label>
              <Input
                placeholder="https://yourcompany.com/favicon.ico"
                value={settings.favicon_url}
                onChange={(e) => setSettings({ ...settings, favicon_url: e.target.value })}
                className="input-dark mt-1"
              />
            </div>

            <div>
              <Label className="text-zinc-300">Primary Color</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer bg-transparent border border-white/10"
                />
                <Input
                  value={settings.primary_color}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  className="input-dark flex-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-300">Secondary Color</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={settings.secondary_color}
                  onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer bg-transparent border border-white/10"
                />
                <Input
                  value={settings.secondary_color}
                  onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                  className="input-dark flex-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-300">Support Email</Label>
              <Input
                type="email"
                placeholder="support@yourcompany.com"
                value={settings.support_email}
                onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                className="input-dark mt-1"
              />
            </div>

            <div>
              <Label className="text-zinc-300">Footer Text</Label>
              <Input
                placeholder="© 2025 Your Company. All rights reserved."
                value={settings.footer_text}
                onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                className="input-dark mt-1"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-white/10">
            <Button 
              onClick={handleSave} 
              disabled={loading}
              className="btn-primary"
              data-testid="whitelabel-save-btn"
            >
              {loading ? <span className="spinner mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Branding Settings
            </Button>
          </div>
        </div>

        {/* Instructions */}
        <div className="glass-card rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Setup Instructions</h3>
          <ol className="space-y-3 text-sm text-zinc-400">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 text-xs">1</span>
              <span>Enter your company name and upload your logo (recommended: 200x50px PNG with transparency)</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 text-xs">2</span>
              <span>Choose your brand colors using the color pickers or enter hex values</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 text-xs">3</span>
              <span>For custom domain setup, add a CNAME record pointing to <code className="bg-zinc-800 px-1 rounded">app.insite360.com</code></span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 text-xs">4</span>
              <span>Contact our support team to verify domain ownership and enable SSL</span>
            </li>
          </ol>
        </div>

        {/* Branded Email Templates */}
        <div className="glass-card rounded-xl p-6" data-testid="whitelabel-templates">
          <TemplateBuilder 
            isWhiteLabel={true}
            onSave={(templates) => {
              console.log('White-label templates saved:', templates);
              toast.success('Branded templates saved!');
            }}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}
